import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { closestIndexTo } from "date-fns";

import api from "../../api";
import { getBabiesSortedByName } from "../../selectors/babies";
import { getSelectedProfile } from "../../selectors/profiles";
import { FormContext } from "../../context/form-context";
import {
  getDietDiaperRecords,
  getGrowthRecords,
  getMilestoneRecords,
  getModuleListLoading,
} from "../../selectors/core";
import { getSelectedBabyId } from "../../selectors/ui";
import { openBabyCreateModal } from "../../actions/ui";

import {
  getEnhancedBasicRecords,
  getEnhancedSleepRecords
} from "../../selectors/records";

import GrowthForm from "./GrowthForm";
import SleepForm from "./SleepForm";
import DietDiaperForm from "./DietDiaperForm";
import MilestoneForm from "./MilestoneForm";
import ActivityForm from "./ActivityForm";
import NoBabyModal from "./NoBabyModal";
import Activities from "./Activities";
import Modal from "../Modal";
import TrackerModal from "./TrackerChooser";
import FormDirtyWarningModal from "./FormDirtyWarningModal";
import SaveEntryModalShown from "./SaveEntryModal";
import { PrimaryButton } from "../buttons";
import { Track } from "../icons";

import "./ActivityTracker.css";

const ActivityTracker = props => {
  const {
    growthRecords,
    dietDiaperRecords,
    selectedProfile,
    profiles,
    sleepRecords,
    sleepRecordsLatest,
    sleepRecordsPatch,
    sleepRecordsPost,
    growthRecordsPatch,
    growthRecordsPost,
    dietDiaperRecordsPatch,
    dietDiaperRecordsPost,
    growthRecordsLatest,
    milestoneRecordsPost,
    milestoneRecordsPatch,
    milestoneRecords,
    milestoneRecordsLatest,
    basicRecords,
    basicRecordsLatest,
    basicRecordsPatch,
    basicRecordsPost,
    accountId,
    babies,
    hideTrackerModals,
    showUnSavedModal,
    setFormValues
  } = props;

  const [profileOptions, setProfileOptions] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [chosenActivity, setChosenActivity] = useState(null);
  const formContext = useContext(FormContext);
  const node = useRef();

  // Decide if current user has ever saved a record of ANY type before, pass it on
  const { dietDiaperRecordsLatest } = props;
  const [showFirstTimeSavingModal, setShowFirstTimeSavingModal] = useState(false);
  const [noBabiesModal, setNoBabiesModal] = useState(false);

  useEffect(() => {
    const allRecords = {
      ...basicRecords,
      ...dietDiaperRecords,
      ...growthRecords,
      ...milestoneRecords,
      ...sleepRecords,
    };

    const hasAuthored = Object.keys(allRecords).some((key) => {
      return allRecords[key].providerId === selectedProfile.id;
    });

    setShowFirstTimeSavingModal(!hasAuthored);
  }, [
    selectedProfile,
    basicRecords,
    dietDiaperRecords,
    growthRecords,
    milestoneRecords,
    sleepRecords
  ]);

  useEffect(() => {
    const formattedProfiles = profiles.map(profile => {
      return {
        label: profile.firstName + " " + profile.lastName,
        value: profile.id
      };
    });

    setProfileOptions(formattedProfiles);
  }, [])

  const getRecordsByType = type => {
    switch (type) {
      case 'growth':
        return growthRecords;
      case 'dietDiaper':
        return dietDiaperRecords;
      case 'milestones':
        return milestoneRecords;
      case 'basic':
        return basicRecords;
      case 'sleep':
        return sleepRecords;
      default:
        return undefined;
    }
  };

  // returns the most recent record for the given type and baby
  // if baby is not provided, just provides the most recent for the given type
  const handleRecords = (type, babyId) => {
    const records = getRecordsByType(type);

    // if they haven't created any records of this type, return undefined
    if (!records) {
      return;
    }

    // convert the object into an array and sort by date
    const recordArray = Object.keys(records)
      .map((key) => records[key])
      .sort((a, b) => {
        const aDate = new Date(a.date).valueOf();
        const bDate = new Date(b.date).valueOf();

        if (aDate === bDate) {
          return 0;
        }

        return aDate > bDate ? -1 : 1;
      });

    // if they provide a babyId parameter, only consider records for that baby
    // otherwise, just use the entire collection
    const babyRecords = babyId
      ? recordArray.filter(r => r.babyId === babyId)
      : recordArray;

    // find either the most recent for the given baby; if none are found, use the most recent overall
    const record = babyRecords.length ? babyRecords[0] : recordArray[0];

    // the value returned from here is uses to pre-populate forms
    // we want to make sure that the babyId/id/time info is not used to prepopulate forms
    // 'delete' sucks and is slow, but just assigning to undefined won't allow for easy assignment with the spread operator
    // ex: {...record, ...overrides}
    const recordClone = {...record};
    delete recordClone.babyId;
    delete recordClone.id;
    delete recordClone.createdAt;
    delete recordClone.updatedAt;
    delete recordClone.date;
    delete recordClone.startTime;
    delete recordClone.endTime;
    delete recordClone.time;

    return recordClone;
  };

  const handleClick = (e) => {
    if (node && node.current.contains(e.target)) {
      return;
    }

    setExpanded(false);
  };

  const toggleWarningUnsaved = () => {
    if (formContext.isDirty) {
      return props.showUnSavedModal();
    } else {
      return props.hideTrackerModals();
    }
  };

  useEffect(() => {
    if (props.accountId) {
      props.babiesList({
        accountId: props.accountId,
      });
    }

    dietDiaperRecordsLatest({ accountId: accountId });
    growthRecordsLatest({ accountId: accountId });
    milestoneRecordsLatest({ accountId: accountId });
    basicRecordsLatest({ accountId: accountId });
    sleepRecordsLatest({ accountId: accountId });

    document.addEventListener("mouseup", handleClick);
    return () => {
      document.removeEventListener("mouseup", handleClick);
    };
  }, [props.accountId]);

  return (
    <div className="activity-tracker__container" ref={node}>
      <TrackerModal
        {...props}
        visible={expanded}
        setExpanded={setExpanded}
        setNoBabiesModal={setNoBabiesModal}
      />

      <NoBabyModal
        noBabiesModal={noBabiesModal}
        setNoBabiesModal={setNoBabiesModal}
        openBabyCreateModal={props.openBabyCreateModal}
      />

      <Modal
        isOpen={props.milestoneModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <MilestoneForm
          {...props}
          milestoneRecordsPost={milestoneRecordsPost}
          milestoneRecordsPatch={milestoneRecordsPatch}
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={props.growthModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <GrowthForm
          {...props}
          selectedProfile={selectedProfile}
          profileOptions={profileOptions}
          growthRecordsPatch={growthRecordsPatch}
          growthRecordsPost={growthRecordsPost}
          showUnSavedModal={showUnSavedModal}
          accountId={accountId}
          babies={babies}
          hideTrackerModals={hideTrackerModals}
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={props.dietDiaperModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <DietDiaperForm
          {...props}
          selectedProfile={selectedProfile}
          profileOptions={profileOptions}
          dietDiaperRecordsPost={dietDiaperRecordsPost}
          dietDiaperRecordsPatch={dietDiaperRecordsPatch}
          accountId={accountId}
          babies={babies}
          hideTrackerModals={hideTrackerModals}
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={props.activitiesModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <Activities {...props} setChosenActivity={setChosenActivity} />
      </Modal>

      <Modal
        isOpen={props.basicModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <ActivityForm
          {...props}
          chosenActivity={chosenActivity}
          profileOptions={profileOptions}
          showUnSavedModal={showUnSavedModal}
          handleRecords={handleRecords}
          basicRecordsPost={basicRecordsPost}
          basicRecordsPatch={basicRecordsPatch}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={props.sleepModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <SleepForm
          {...props}
          showUnSavedModal={showUnSavedModal}
          sleepRecordsPost={sleepRecordsPost}
          sleepRecordsPatch={sleepRecordsPatch}
          profileOptions={profileOptions}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
          handleRecords={handleRecords}
          handleFormValues={setFormValues}
        />
      </Modal>

      <SaveEntryModalShown {...props} />
      <FormDirtyWarningModal {...props} />
      <PrimaryButton
        colorScheme="basic"
        type="button"
        open={expanded}
        label={
          <Track width="32" height="32" viewBox="0 0 32 32" label="track" />
        }
        onClick={() => setExpanded(!expanded)}
      />
    </div>
  );
}

ActivityTracker.defaultProps = {
  showTrackerModal: () => {},
  trackerModalShown: false,
  hideTrackerModals: () => {},
  milestoneModalShown: false,
  dietDiaperModalShown: false,
  growthModalShown: false,
  visitationModalShown: false,
  sleepModalShown: false,
  saveEntryModalShown: false,
  unSavedModalShown: false,
  showMilestoneModal: () => {},
  showUnSavedModal: () => {},
  showDietDiaperModal: () => {},
  showGrowthModal: () => {},
  showVisitationModal: () => {},
  showSleepModal: () => {},
  showSaveEntryModal: () => {},
  t: () => {},
};

ActivityTracker.propTypes = {
  showTrackerModal: PropTypes.func,
  trackerModalShown: PropTypes.bool,
  hideTrackerModals: PropTypes.func,
  milestoneModalShown: PropTypes.bool,
  dietDiaperModalShown: PropTypes.bool,
  growthModalShown: PropTypes.bool,
  visitationModalShown: PropTypes.bool,
  sleepModalShown: PropTypes.bool,
  saveEntryModalShown: PropTypes.bool,
  unSavedModalShown: PropTypes.bool,
  showMilestoneModal: PropTypes.func,
  showDietDiaperModal: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  showGrowthModal: PropTypes.func,
  showVisitationModal: PropTypes.func,
  showSleepModal: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  t: PropTypes.func
};

const mapStateToProps = (state) => ({
  babies: getBabiesSortedByName(state),
  selectedBabyId: getSelectedBabyId(state),
  selectedProfile: getSelectedProfile(state),
  basicRecords: getEnhancedBasicRecords(state),
  dietDiaperRecords: getDietDiaperRecords(state),
  growthRecords: getGrowthRecords(state),
  milestoneRecords: getMilestoneRecords(state),
  sleepRecords: getEnhancedSleepRecords(state),
  modulesListLoading: getModuleListLoading(state)
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      basicRecordsLatest: api.actions.basicRecordsLatest,
      basicRecordsPost: api.actions.basicRecordsPost,
      basicRecordsPatch: api.actions.basicRecordsPatch,
      growthRecordsPost: api.actions.growthRecordsPost,
      growthRecordsLatest: api.actions.growthRecordsLatest,
      growthRecordsPatch: api.actions.growthRecordsPatch,
      dietDiaperRecordsLatest: api.actions.dietDiaperRecordsLatest,
      dietDiaperRecordsPost: api.actions.dietDiaperRecordsPost,
      dietDiaperRecordsPatch: api.actions.dietDiaperRecordsPatch,
      milestoneRecordsLatest: api.actions.milestoneRecordsLatest,
      milestoneRecordsPost: api.actions.milestoneRecordsPost,
      milestoneRecordsPatch: api.actions.milestoneRecordsPatch,
      sleepRecordsLatest: api.actions.sleepRecordsLatest,
      sleepRecordsPost: api.actions.sleepRecordsPost,
      sleepRecordsPatch: api.actions.sleepRecordsPatch,
      openBabyCreateModal: openBabyCreateModal,
      showUnSavedModal: api.actions.showUnSavedModal,
      handleFormValues: api.actions.handleFormValues
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTracker);



// WEBPACK FOOTER //
// ./src/components/ActivityTracker/ActivityTracker.js